import { useNavigate } from 'react-router-dom'

interface CalculatorLinkProps {
  children: React.ReactNode
  propertyPrice?: number
  downPaymentPercent?: number
  loanYears?: number
  wiborRate?: number
  bankMargin?: number
  monthlyOverpayment?: number
  monthlyRent?: number
  installmentType?: 'equal' | 'decreasing'
  /** Scroll to a specific calculator section: 'installment-type' | 'interest-rate' | 'affordability' | 'overpayment' | 'rent-vs-buy' */
  section?: string
}

export function CalculatorLink({
  children,
  propertyPrice,
  downPaymentPercent,
  loanYears,
  wiborRate,
  bankMargin,
  monthlyOverpayment,
  monthlyRent,
  installmentType,
  section,
}: CalculatorLinkProps) {
  const navigate = useNavigate()

  const params = new URLSearchParams()
  if (propertyPrice !== undefined) params.set('p', String(propertyPrice))
  if (downPaymentPercent !== undefined) params.set('d', String(downPaymentPercent))
  if (loanYears !== undefined) params.set('y', String(loanYears))
  if (wiborRate !== undefined) params.set('w', String(wiborRate))
  if (bankMargin !== undefined) params.set('m', String(bankMargin))
  if (monthlyOverpayment !== undefined && monthlyOverpayment > 0) params.set('o', String(monthlyOverpayment))
  if (monthlyRent !== undefined && monthlyRent > 0) params.set('r', String(monthlyRent))
  if (installmentType && installmentType !== 'equal') params.set('t', installmentType)

  const query = params.toString()
  const hash = section ? `#${section}` : ''
  const to = query ? `/?${query}${hash}` : `/${hash}`

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    navigate(to)
    // Scroll after React has rendered the new route, then clean URL
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (section) {
          const el = document.getElementById(section)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'instant' })
        }
        // Clean hash from URL
        window.history.replaceState({}, '', '/')
      })
    })
  }

  return (
    <a
      href={to}
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 text-primary font-semibold hover:underline underline-offset-2"
    >
      {children}
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
      </svg>
    </a>
  )
}
